import React, { PureComponent } from 'react';
import {
    Text,
    TouchableOpacity,
    View,
    Platform,
    ActivityIndicator,
    TextInput,
    FlatList,
    Image,
    Picker
} from 'react-native-web';
import PropTypes from 'prop-types';
import Icon from 'react-native-vector-icons/MaterialIcons';

import { CityPickerModal, InScreenModal, NavBar } from '../../components';
import LoadingView from '../../components/component_loading_view';

import { showAppError } from '../../utils/util_helpers';
import { getActivityList } from '../../modules/api_activities';

import GLOBAL from '../../configs/config_global';

export default class ActivitySearchModal extends PureComponent {
    // region Props
    static propTypes = {
        onActivitySelected: PropTypes.func
    };

    static defaultProps = {
        onActivitySelected: () => {}
    };
    // endregion

    // region Constructor
    state = {
        showModal: false,
        searchText: '',
        isLoadingLocation: false,
        isLoadingActivities: false,
        isLoadingActivityCoordinates: false,
        activityResult: [],
        typing: false,
        typingTimeout: null,
        totalResult: 0,
        currentPage: 0,
        lastPage: 0,
        selectedCityID: GLOBAL.MAIN?.state.truckData.city?.[0]?.id || 17828
    };
    // endregion

    // region Function - Hide Modal
    hideModal = () => this.setState({ showModal: false });
    showModal = () => this.setState({ showModal: true });
    // endregion

    // region Function - Pick Activity
    pickActivity(activityData) {
        let upcomingEventData = activityData.events[0] || {};

        this.props.onActivitySelected({
            breweryData: null,
            activityData,
            address: upcomingEventData['address'],
            latitude: Number(activityData['latitude']),
            longitude: Number(activityData['longtitude'])
        });

        // Close modal
        this.hideModal();
    }
    // endregion

    // region Function - Search for activities
    searchForActivities(text) {
        // If nothing was input, ignore
        if (!text.trim()) {
            this.setState({ activityResult: [], searchText: '', currentPage: 0, lastPage: 0, totalResult: 0 });
            return;
        }

        // Start searching
        this.setState({ isLoadingActivities: true, searchText: text, activityResult: [], typing: false, currentPage: 0, lastPage: 0, totalResult: 0 }, () => {
            this.searchInput?.blur();

            // Start calling API
            getActivityList(1, 20, this.state.searchText, GLOBAL.CITIES[this.state.selectedCityID].slug, GLOBAL.CITIES[this.state.selectedCityID].state.short_name)
                .then((resultObj) => {
                    this.setState({
                        isLoadingActivities: false,
                        activityResult: resultObj.data,
                        currentPage: resultObj.currentPage,
                        lastPage: resultObj.lastPage,
                        totalResult: resultObj.totalResult
                    },
                    () => {
                        if (this.state.activityResult.length > 0) {
                            this.activityFlatlist?.scrollToIndex({ index: 0, animated: false });
                        }
                    });
                })
                .catch((error) => {
                    this.setState({ isLoadingActivities: false, activityResult: [] });
                    showAppError(error);
                });
        });
    }

    keepFetchingActivityData() {
        // Cancel if there is no internet connection
        if (!GLOBAL.MAIN?.state.isConnected || (this.state.currentPage >= this.state.lastPage) || this.state.isLoadingActivities) return;

        // Start calling API
        this.setState({ isLoadingActivities: true });
        getActivityList(this.state.currentPage + 1, 20, this.state.searchText, GLOBAL.CITIES[this.state.selectedCityID].slug, GLOBAL.CITIES[this.state.selectedCityID].state.short_name)
            .then((resultObj) => {
                this.setState({
                    activityResult: this.state.activityResult.concat(resultObj.data),
                    currentPage: resultObj.currentPage,
                    lastPage: resultObj.lastPage,
                    totalResult: resultObj.totalResult,
                    isLoadingActivities: false
                });
            })
            .catch((error) => {
                this.setState({ isLoadingActivities: false });

                // Inform the user
                GLOBAL.MAIN?.toast.show('Unable to fetch more data from server.\n\nError message: ' + error, 3000);
            });
    }
    // endregion

    // region Render Search Bar
    renderSearchBar() {
        return(
            <View
                ref={(comp) => this.activitySearchBar = comp}
                style={[{
                    alignSelf: 'stretch', alignItems: 'center', flexDirection: 'row',
                    backgroundColor: GLOBAL.COLOR.BASIC.WHITE_TWO,
                    borderRadius: 5, borderColor: 'rgba(0, 0, 0, 0.08)', height: 64,
                    margin: 16, marginTop: 0, paddingHorizontal: 8
                }]}>

                {/* Search Icon */}
                {
                    this.state.isLoadingActivities ?
                        <ActivityIndicator
                            animating
                            color={GLOBAL.COLOR.MAIN}
                            style={{ marginHorizontal: 4 }}
                            size={32}
                        />
                        :
                        <Icon
                            name={'search'}
                            size={32}
                            color={this.state.searchText ? GLOBAL.COLOR.TEXT_GRAY : GLOBAL.COLOR.BASIC.PINKISH_GREY}
                            style={{ marginLeft: 4 }}
                        />
                }

                {/* Search Text Input */}
                <TextInput
                    ref={(comp) => this.searchInput = comp}
                    autoCorrect={false}
                    onChangeText={(text) => {
                        const self = this;
                        if (self.state.typingTimeout) {
                            clearTimeout(self.state.typingTimeout);
                        }

                        if (!text.trim()) {
                            self.setState({ searchText: '', activityResult: [], typing: false });
                            return;
                        }

                        self.setState(Object.assign({
                            typing: true,
                            typingTimeout: setTimeout(() => this.searchForActivities(text), 750),
                            searchText: text
                        }));
                    }}
                    onSubmitEditing={() => {
                        if (this.state.typingTimeout) clearTimeout(this.state.typingTimeout);
                        this.searchForActivities(this.state.searchText);
                    }}
                    value={this.state.searchText}
                    editable={!this.state.isLoadingLocation}
                    placeholder={`Enter activity name, address, etc.`}
                    placeholderTextColor={GLOBAL.COLOR.BASIC.PINKISH_GREY}
                    underlineColorAndroid={'transparent'}
                    style={{ outlineStyle: 'none', fontSize: 18, color: GLOBAL.COLOR.BASIC.WARM_GREY, marginLeft: 8, flex: 1, marginVertical: Platform.OS === 'android' ? -5 : 0 }}
                />

                {/* Clear Text Button */}
                {
                    (this.state.searchText.trim() || (this.state.currentPage > 0)) &&
                    <TouchableOpacity
                        style={{ marginRight: 2 }}
                        onPress={() => this.setState(Object.assign({ activityResult: [], searchText: '', currentPage: 0, totalPage: 0, lastPage: 0 }))}>
                        <Icon
                            name={'close'}
                            size={28}
                            color={GLOBAL.COLOR.BASIC.PINKISH_GREY}
                        />
                    </TouchableOpacity>
                }

                {/* City Selector */}
                <TouchableOpacity
                    onPress={() => this.cityPicker?.showModal()}
                    style={{ flexDirection: 'row', alignItems: 'center', marginRight: 8 }}>
                    {/* Divider */}
                    <View style={{ height: 24, width: 1, marginVertical: 2, backgroundColor: GLOBAL.COLOR.TEXT_LIGHT_GRAY, marginRight: 16, marginLeft: 8 }}/>

                    {/* City Name */}
                    <Text style={{ color: 'black', fontWeight: '500' }}>
                        {GLOBAL.CITIES[this.state.selectedCityID].name}
                    </Text>
                </TouchableOpacity>
            </View>
        );
    }
    // endregion

    // region Render Activity Card
    renderActivityCard(item) {
        let logoSize = 40;

        return(
            <TouchableOpacity
                onPress={() => this.pickActivity(item)}
                style={{ width: '100%', flexDirection: 'row', alignItems: 'center', borderBottomWidth: 1, borderColor: GLOBAL.COLOR.DIVIDER, padding: 16 }}>
                {/* Image */}
                <View style={{ backgroundColor: GLOBAL.COLOR.BASIC.UI_BLUE, width: logoSize, height: logoSize, borderRadius: logoSize / 2, alignItems: 'center', justifyContent: 'center' }}>
                    <Image
                        source={require('../../images/iconLineDate.png')}
                        style={{ width: 22, height: 22, tintColor: 'white' }}
                    />
                </View>

                {/* Info */}
                <View style={{ flex: 1, marginLeft: 16 }}>
                    {/* Name and Rating */}
                    <Text style={{ fontWeight: '500', fontSize: 18, alignSelf: 'stretch' }} numberOfLines={2}>
                        {item.name}
                    </Text>

                    {/* Location */}
                    <Text style={{ color: GLOBAL.COLOR.TEXT_GRAY, alignSelf: 'stretch', marginTop: 8 }} numberOfLines={1}>
                        {item.address?.split(',')[0] || ''}
                    </Text>
                </View>
            </TouchableOpacity>
        );
    }
    // endregion

    // region Render City Picker
    renderCityPicker() {
        return(
            <CityPickerModal
                ref={(comp) => (this.cityPicker = comp)}
                singleSelect={true}
                liteMode={true}
                onCitySelected={(id) => this.setState({ selectedCityID: id })}
            />
        )
    }
    // endregion

    // region MAIN RENDER FUNCTION
    render() {
        return(
            <InScreenModal visible={this.state.showModal}>
                {/* Nav Bar */}
                <NavBar
                    title={'Activity Search'}
                    horizontalSpacing={16}
                    hideBorder={true}
                    leftButtonOnPress={() => this.hideModal()}
                    leftButtonIconName={'close'}
                    ignoreTopBarHeight={true}
                />

                {/* Content */}
                <View style={{ flex: 1, width: '100%', backgroundColor: 'white' }}>
                    {/* Search Bar */}
                    {this.renderSearchBar()}

                    {this.renderCityPicker()}

                    {/* Activity List */}
                    {
                        (this.state.activityResult.length > 0) ?
                            <FlatList
                                ref={(comp) => this.activityFlatlist = comp}
                                style={{ width: '100%', flex: 1 }}
                                contentContainerStyle={{ paddingBottom: 24 }}
                                data={this.state.activityResult}
                                renderItem={({ item }) => this.renderActivityCard(item)}
                                showsVerticalScrollIndicator={false}
                                keyExtractor={(item) => item.id.toString()}
                                onEndReached={() => this.keepFetchingActivityData()}
                                onEndReachedThreshold={0.2}
                                ListFooterComponent={() => (
                                    (this.state.isLoadingActivities && this.state.currentPage > 0) ?
                                        <View style={{ flexDirection: 'row', alignItems: 'center', padding: 16, justifyContent: 'center' }}>
                                            <ActivityIndicator color={GLOBAL.COLOR.MAIN}/>

                                            <Text style={{ fontWeight: '500', color: GLOBAL.COLOR.MAIN, fontSize: 15, marginLeft: 8 }}>
                                                Fetching more activities
                                            </Text>
                                        </View>
                                        :
                                        null
                                )}
                            />
                            :
                            <View style={{ flex: 1, width: '100%', alignItems: 'center', justifyContent: 'center' }}>
                                <Text style={{ textAlign: 'center', alignSelf: 'center', color: GLOBAL.COLOR.TEXT_LIGHT_GRAY }}>
                                    {(this.state.currentPage > 0) ? 'No results found' : (this.state.typing ? '' : 'Start typing to search')}
                                </Text>
                            </View>
                    }
                </View>

                {/* Loading View */}
                {this.state.isLoadingActivityCoordinates && <LoadingView isAbsolute={true} description={'Retrieving location data'}/>}
            </InScreenModal>
        );
    }
    // endregion
}
